import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  MetricUsersIcon,
  TrendingUpIcon,
  UserPlusIcon,
  MaximizeIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from '../../Icons/DashboardIcons';

// Import refactored chart components
import UserGrowthChart from './charts/UserGrowthChart';
import IndividualTLFootfallChart from './charts/IndividualTLFootfallChart';
import DepartmentDistributionChart from './charts/DepartmentDistributionChart';
import GenderStatsChart from './charts/GenderStatsChart';

// Import reusable components
import { FullscreenChartModal, ErrorDisplay, SampleDataBadge } from './common';

// Import API
import TLDashboardAPIs from '../../utilities/api/dashboard/TLDashboardAPIs';

// Import sample data utilities
import { isDataEmpty, getSampleDataForPeriod } from '../../utilities/sampleData/tlDashboardSampleData';


// ---------------------------------------------------------------------------
// DEMO DATA for the metric cards.
// While USE_DEMO_METRICS is true, all four cards IGNORE the API response and
// show demo numbers that depend on who is viewing:
//   - MBF admin -> organisation-wide numbers (fixed)
//   - each TL   -> their own team numbers (different per tlId, stable across reloads)
// TODO: set USE_DEMO_METRICS to false (or delete this block) after the demo.
// ---------------------------------------------------------------------------
const USE_DEMO_METRICS = true;

const MBF_ADMIN_DEMO = {
  total_users: 3420,
  weekly: { growth_percentage: '+4.3%', new_users_this_period: 142, total_footfall: 24860 },
  monthly: { growth_percentage: '+16.6%', new_users_this_period: 486, total_footfall: 103450 },
  yearly: { growth_percentage: '+52.7%', new_users_this_period: 1180, total_footfall: 1248300 },
};

// Footfall per user in each period (used to build TL demo data)
const FOOTFALL_PER_USER = { weekly: 9, monthly: 38, yearly: 440 };
// Share of the team that is "new" in each period: [min, max]
const NEW_USER_SHARE = { weekly: [0.03, 0.1], monthly: [0.08, 0.2], yearly: [0.2, 0.4] };

// Tiny deterministic hash/random so the same TL always gets the same numbers
const hashString = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
};
const seededRandom = (seed) => {
  let t = (seed + 0x6d2b79f5) >>> 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const getDemoMetrics = ({ period, isMbfAdmin, tlId }) => {
  const p = MBF_ADMIN_DEMO[period] && period !== 'total_users' ? period : 'weekly';

  if (isMbfAdmin) {
    return { total_users: MBF_ADMIN_DEMO.total_users, ...MBF_ADMIN_DEMO[p] };
  }

  const seed = hashString(String(tlId ?? 'default'));
  const totalUsers = 30 + Math.floor(seededRandom(seed + 1) * 90); // 30-119 users in the team
  const [minShare, maxShare] = NEW_USER_SHARE[p];
  const share = minShare + seededRandom(seed + hashString(p)) * (maxShare - minShare);
  const newUsers = Math.max(1, Math.round(totalUsers * share));
  const growth = (newUsers / Math.max(1, totalUsers - newUsers)) * 100;
  const footfallFactor = 0.7 + seededRandom(seed + 7) * 0.6; // 0.7-1.3
  const footfall = Math.round(totalUsers * FOOTFALL_PER_USER[p] * footfallFactor);

  return {
    total_users: totalUsers,
    growth_percentage: `+${growth.toFixed(1)}%`,
    new_users_this_period: newUsers,
    total_footfall: footfall,
  };
};

const UserStatisticsTab = ({ selectedPeriod = 'weekly', isMbfAdmin = false, tlId = null }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [maximizedChart, setMaximizedChart] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch data when period or refreshTrigger changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await TLDashboardAPIs.getUserStatistics(selectedPeriod);
        setData(response);
      } catch (err) {
        console.error('Error fetching user statistics:', err);
        setError('Failed to load user statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedPeriod, refreshTrigger]);

  const handleRetry = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Get period label for display
  const getPeriodLabel = () => {
    switch (selectedPeriod) {
      case 'weekly': return 'This Week';
      case 'monthly': return 'This Month';
      case 'yearly': return 'This Year';
      default: return 'This Period';
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          gap: 2
        }}
      >
        <CircularProgress size={48} sx={{ color: '#007bff' }} />
        <Typography sx={{ color: '#666', fontSize: '14px', fontWeight: 500 }}>
          Fetching user statistics...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return <ErrorDisplay message={error} onRetry={handleRetry} />;
  }
  // / Extract data from API response with sample data fallback
  // const totalUsers = data?.total_users || 0;
  // const growthPercentage = data?.growth_percentage || '+0%';
  // const newUsersThisPeriod = data?.new_users_this_period || 0;
  // const totalFootfall = data?.total_footfall || 0;

  // Demo numbers for this viewer (MBF admin or a specific TL) and period
  // Use the tlId prop if the parent passes it, otherwise try ids from the API response
  const demoKey =
    tlId ?? data?.tl_id ?? data?.team_leader_id ?? data?.user_id ?? data?.id ?? null;
  const demo = getDemoMetrics({ period: selectedPeriod, isMbfAdmin, tlId: demoKey });

  if (USE_DEMO_METRICS) {
    // DEBUG: if demoKey is null, every TL gets the same numbers (nothing identifies the TL)
    console.log('[UserStatisticsTab demo]', {
      tlIdProp: tlId,
      demoKey,
      apiResponseKeys: Object.keys(data || {}),
    });
  }

  // While demo mode is on, the API response is ignored for all four cards
  const totalUsers = USE_DEMO_METRICS ? demo.total_users : Number(data?.total_users) || 0;

  const growthPercentage = USE_DEMO_METRICS
    ? demo.growth_percentage
    : data?.growth_percentage || '+0%';

  const newUsersThisPeriod = USE_DEMO_METRICS
    ? demo.new_users_this_period
    : data?.new_users_this_period || 0;

  const totalFootfall = USE_DEMO_METRICS
    ? demo.total_footfall
    : data?.total_footfall || 0;

  // Gender stats with sample data fallback
  const rawGenderStats = data?.gender_stats || { male: 0, female: 0, male_percentage: '0%', female_percentage: '0%' };
  const isGenderStatsEmpty = rawGenderStats.male === 0 && rawGenderStats.female === 0;
  const genderStats = isGenderStatsEmpty ? getSampleDataForPeriod('genderStats') : rawGenderStats;

  // User growth trend with sample data fallback
  const rawUserGrowthTrend = data?.user_growth_trend || [];
  const isUserGrowthEmpty = isDataEmpty(rawUserGrowthTrend, 'array');
  const userGrowthTrend = isUserGrowthEmpty ? getSampleDataForPeriod('userGrowthTrend', selectedPeriod) : rawUserGrowthTrend;

  // Individual TL Footfall with sample data fallback
  const rawIndividualTLFootfall = data?.individual_tl_footfall || [];
  const isFootfallEmpty = isDataEmpty(rawIndividualTLFootfall, 'array');
  const individualTLFootfall = isFootfallEmpty ? getSampleDataForPeriod('individualFootfall', selectedPeriod) : rawIndividualTLFootfall;

  // Department distribution with sample data fallback
  const rawDepartmentDistribution = data?.department_distribution || [];
  const isDeptDistEmpty = isDataEmpty(rawDepartmentDistribution, 'array');
  const departmentDistribution = isDeptDistEmpty ? getSampleDataForPeriod('departmentDistribution') : rawDepartmentDistribution;

  // Helper function to get chart title
  const getChartTitle = (chartType) => {
    switch (chartType) {
      case 'genderStats': return 'Gender Stats';
      case 'userGrowth': return 'User Growth Trend';
      case 'tlFootfall': return 'Individual TL Footfall';
      case 'departmentDistribution': return 'Distribution Across Departments';
      default: return '';
    }
  };

  // Helper function to render chart content
  const renderChartContent = (chartType) => {
    switch (chartType) {
      case 'genderStats':
        return (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '24px',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: '70%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <GenderStatsChart
                male={genderStats.male}
                female={genderStats.female}
                malePercentage={genderStats.male_percentage}
                femalePercentage={genderStats.female_percentage}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: '40px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box
                  sx={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    bgcolor: '#ffb3cf',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 500,
                    color: '#666666',
                    fontFamily: '"Inter", sans-serif',
                  }}
                >
                  Female ({genderStats.female})
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box
                  sx={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    bgcolor: '#8ccbff',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 500,
                    color: '#666666',
                    fontFamily: '"Inter", sans-serif',
                  }}
                >
                  Male ({genderStats.male})
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      case 'userGrowth':
        return (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <UserGrowthChart data={userGrowthTrend} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  color: '#666666',
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 500,
                }}
              >
                {selectedPeriod === 'weekly' ? 'Days' : selectedPeriod === 'monthly' ? 'Days' : 'Months'}
              </Typography>
            </Box>
          </Box>
        );
      case 'tlFootfall':
        return (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <IndividualTLFootfallChart data={individualTLFootfall} />
            </Box>
          </Box>
        );
      case 'departmentDistribution':
        return (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DepartmentDistributionChart data={departmentDistribution} />
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Fullscreen Chart Modal */}
      <FullscreenChartModal
        open={Boolean(maximizedChart)}
        onClose={() => setMaximizedChart(null)}
        title={getChartTitle(maximizedChart)}
      >
        {renderChartContent(maximizedChart)}
      </FullscreenChartModal>
      {/* Metrics Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: { xs: '12px', sm: '16px', md: '24px' },
        }}
      >
        {/* Total Users Card */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #c144ff',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: { xs: 'auto', md: '107px' },
            minHeight: { xs: '90px', md: '107px' },
            boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <Box
              sx={{
                width: '4px',
                height: '42px',
                bgcolor: '#c144ff',
                borderRadius: '4px',
                flexShrink: 0,
              }}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '24px', sm: '28px', md: '32px' },
                  fontWeight: 700,
                  fontFamily: '"Inter", sans-serif',
                  color: '#212121',
                  lineHeight: 1.1,
                  textAlign: 'left',
                }}
              >
                {totalUsers.toLocaleString()}
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  fontFamily: '"Inter", sans-serif',
                  color: '#666666',
                  textAlign: 'left',
                  mt: '4px',
                }}
              >
                Total Users
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              width: '44px',
              height: '44px',
              bgcolor: '#F3E8FF',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <MetricUsersIcon color="#c144ff" />
          </Box>
        </Box>

        {/* Growth Card */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #12ab0a',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: { xs: 'auto', md: '107px' },
            minHeight: { xs: '90px', md: '107px' },
            boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <Box
              sx={{
                width: '4px',
                height: '42px',
                bgcolor: '#12ab0a',
                borderRadius: '4px',
                flexShrink: 0,
              }}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                <Typography
                  sx={{
                    fontSize: { xs: '24px', sm: '28px', md: '32px' },
                    fontWeight: 700,
                    fontFamily: '"Inter", sans-serif',
                    color: '#212121',
                    lineHeight: 1.1,
                    textAlign: 'left',
                  }}
                >
                  {growthPercentage}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  fontFamily: '"Inter", sans-serif',
                  color: '#666666',
                  textAlign: 'left',
                  mt: '4px',
                }}
              >
                Growth
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              width: '44px',
              height: '44px',
              bgcolor: '#E7FFEB',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <TrendingUpIcon color="#12ab0a" />
          </Box>
        </Box>

        {/* New Users This Period Card */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #f99f24',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: { xs: 'auto', md: '107px' },
            minHeight: { xs: '90px', md: '107px' },
            boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <Box
              sx={{
                width: '4px',
                height: '42px',
                bgcolor: '#f99f24',
                borderRadius: '4px',
                flexShrink: 0,
              }}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '24px', sm: '28px', md: '32px' },
                  fontWeight: 700,
                  fontFamily: '"Inter", sans-serif',
                  color: '#212121',
                  lineHeight: 1.1,
                  textAlign: 'left',
                }}
              >
                {newUsersThisPeriod.toLocaleString()}
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  fontFamily: '"Inter", sans-serif',
                  color: '#666666',
                  textAlign: 'left',
                  mt: '4px',
                }}
              >
                New Users {getPeriodLabel()}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              width: '44px',
              height: '44px',
              bgcolor: '#FFE8D1',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <UserPlusIcon color="#f99f24" />
          </Box>
        </Box>

        {/* Total Footfall Card */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #ff448c',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: { xs: 'auto', md: '107px' },
            minHeight: { xs: '90px', md: '107px' },
            boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <Box
              sx={{
                width: '4px',
                height: '42px',
                bgcolor: '#ff448c',
                borderRadius: '4px',
                flexShrink: 0,
              }}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                <Typography
                  sx={{
                    fontSize: { xs: '24px', sm: '28px', md: '32px' },
                    fontWeight: 700,
                    fontFamily: '"Inter", sans-serif',
                    color: '#212121',
                    lineHeight: 1.1,
                    textAlign: 'left',
                  }}
                >
                  {totalFootfall.toLocaleString()}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  fontFamily: '"Inter", sans-serif',
                  color: '#666666',
                  textAlign: 'left',
                  mt: '4px',
                }}
              >
                Total Footfall
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              width: '44px',
              height: '44px',
              bgcolor: '#FFE4EF',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <TrendingUpIcon color="#ff448c" />
          </Box>
        </Box>
      </Box>

      {/* Charts Section */}
      <Box
        sx={{
          mt: { xs: 2, md: 3 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1.5fr' },
          gap: { xs: '16px', md: '24px' },
        }}
      >
        {/* Gender Stats */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: { xs: '8px', md: '12px' },
            border: '1px solid #e6e6e6',
            boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            height: { xs: '320px', sm: '380px', md: '480px' },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              padding: { xs: '8px 10px', md: '10px 12px' },
              borderBottom: '1px solid #e6e6e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                sx={{
                  fontSize: { xs: '14px', md: '16px' },
                  fontWeight: 500,
                  fontFamily: '"Inter", sans-serif',
                  color: '#333333',
                }}
              >
                Gender Stats
              </Typography>
              <SampleDataBadge show={isGenderStatsEmpty} absolute={false} />
            </Box>
            <Box
              onClick={() => setMaximizedChart('genderStats')}
              sx={{
                padding: { xs: '6px', md: '8px' },
                border: '1px solid #e6e6e6',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  borderColor: '#c144ff',
                },
              }}
            >
              <MaximizeIcon />
            </Box>
          </Box>
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              flex: 1,
              minHeight: 0,
              display: 'flex',
            }}
          >
            <Box
              sx={{
                flex: 1,
                border: '1px solid #e0e0e0',
                borderRadius: '20px',
                padding: { xs: '12px', md: '20px' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: '12px', md: '16px' },
                bgcolor: '#ffffff',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <GenderStatsChart
                  male={genderStats.male}
                  female={genderStats.female}
                  malePercentage={genderStats.male_percentage}
                  femalePercentage={genderStats.female_percentage}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: { xs: '20px', md: '40px' }, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Box
                    sx={{
                      width: { xs: '10px', md: '12px' },
                      height: { xs: '10px', md: '12px' },
                      borderRadius: '50%',
                      bgcolor: '#ffb3cf',
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px', md: '16px' },
                      fontWeight: 500,
                      color: '#666666',
                      fontFamily: '"Inter", sans-serif',
                    }}
                  >
                    Female ({genderStats.female})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Box
                    sx={{
                      width: { xs: '10px', md: '12px' },
                      height: { xs: '10px', md: '12px' },
                      borderRadius: '50%',
                      bgcolor: '#8ccbff',
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px', md: '16px' },
                      fontWeight: 500,
                      color: '#666666',
                      fontFamily: '"Inter", sans-serif',
                    }}
                  >
                    Male ({genderStats.male})
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* User Growth Trend */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: { xs: '8px', md: '12px' },
            border: '1px solid #e6e6e6',
            boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            height: { xs: '320px', sm: '380px', md: '480px' },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              borderBottom: '1px solid #e6e6e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                sx={{
                  fontSize: { xs: '14px', md: '16px' },
                  fontWeight: 500,
                  fontFamily: '"Inter", sans-serif',
                  color: '#333333',
                }}
              >
                User Growth Trend
              </Typography>
              <SampleDataBadge show={isUserGrowthEmpty} absolute={false} />
            </Box>
            <Box
              onClick={() => setMaximizedChart('userGrowth')}
              sx={{
                padding: { xs: '6px', md: '8px' },
                border: '1px solid #e6e6e6',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  borderColor: '#0C597D',
                },
              }}
            >
              <MaximizeIcon />
            </Box>
          </Box>
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              flex: 1,
              minHeight: 0,
              display: 'flex',
            }}
          >
            <Box
              sx={{
                flex: 1,
                border: '1px solid #e0e0e0',
                borderRadius: '20px',
                padding: { xs: '12px', md: '20px' },
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#ffffff',
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <UserGrowthChart data={userGrowthTrend} />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'relative',
                  mt: { xs: 1, md: 2 },
                  minHeight: '24px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '12px', md: '14px' },
                    color: '#666666',
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 500,
                  }}
                >
                  {selectedPeriod === 'weekly' ? 'Days' : selectedPeriod === 'monthly' ? 'Days' : 'Months'}
                </Typography>
                <Box
                  sx={{
                    position: 'absolute',
                    right: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: '20px', md: '30px' },
                      height: '3px',
                      bgcolor: '#E0F5FFB2',
                      borderRadius: '2px',
                      border: '1.5px solid #0C597D',
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: '12px', md: '14px' },
                      color: '#222222',
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    Users
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Second Row Charts */}
      <Box
        sx={{
          mt: { xs: 2, md: 3 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: { xs: '16px', md: '24px' },
        }}
      >
        {/* Individual TL Footfall */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: { xs: '8px', md: '12px' },
            border: '1px solid #e6e6e6',
            boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            height: { xs: '280px', sm: '320px', md: '380px' },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              borderBottom: '1px solid #e6e6e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                sx={{
                  fontSize: { xs: '14px', md: '16px' },
                  fontWeight: 500,
                  fontFamily: '"Inter", sans-serif',
                  color: '#333333',
                }}
              >
                Individual TL Footfall
              </Typography>
              <SampleDataBadge show={isFootfallEmpty} absolute={false} />
            </Box>
            <Box
              onClick={() => setMaximizedChart('tlFootfall')}
              sx={{
                padding: { xs: '6px', md: '8px' },
                border: '1px solid #e6e6e6',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  borderColor: '#12ab0a',
                },
              }}
            >
              <MaximizeIcon />
            </Box>
          </Box>
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              flex: 1,
              minHeight: 0,
              display: 'flex',
            }}
          >
            <Box
              sx={{
                flex: 1,
                border: '1px solid #e0e0e0',
                borderRadius: '20px',
                padding: { xs: '12px', md: '20px' },
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#ffffff',
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <IndividualTLFootfallChart data={individualTLFootfall} />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: '8px',
                  mt: { xs: 1, md: 2 },
                }}
              >
                <Box
                  sx={{
                    padding: '4px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    bgcolor: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <ArrowLeftIcon />
                </Box>
                <Box
                  sx={{
                    padding: '4px 12px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    bgcolor: '#ffffff',
                  }}
                >
                  <Typography sx={{ fontSize: '12px', color: '#666666', fontWeight: 500 }}>
                    {data?.period_start && data?.period_end
                      ? `${new Date(data.period_start).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })} - ${new Date(data.period_end).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })}`
                      : '01/02'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    padding: '4px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    bgcolor: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <ArrowRightIcon />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Distribution Across Departments */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderRadius: { xs: '8px', md: '12px' },
            border: '1px solid #e6e6e6',
            boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            height: { xs: '280px', sm: '320px', md: '380px' },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              borderBottom: '1px solid #e6e6e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                sx={{
                  fontSize: { xs: '14px', md: '16px' },
                  fontWeight: 500,
                  fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
                  color: '#333333',
                }}
              >
                Distribution Across Departments
              </Typography>
              <SampleDataBadge show={isDeptDistEmpty} absolute={false} />
            </Box>
            <Box
              onClick={() => setMaximizedChart('departmentDistribution')}
              sx={{
                padding: { xs: '6px', md: '8px' },
                border: '1px solid #e6e6e6',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  borderColor: '#f99f24',
                },
              }}
            >
              <MaximizeIcon />
            </Box>
          </Box>
          <Box
            sx={{
              padding: { xs: '10px', md: '12px' },
              flex: 1,
              minHeight: 0,
              display: 'flex',
            }}
          >
            <Box
              sx={{
                flex: 1,
                border: '1px solid #e0e0e0',
                borderRadius: '20px',
                padding: { xs: '12px', md: '20px' },
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#ffffff',
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <DepartmentDistributionChart data={departmentDistribution} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UserStatisticsTab;